import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, InputAdornment, Stack, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from "@mui/material"
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined"
import SendIcon from "@mui/icons-material/SendOutlined"
import WarningIcon from "@mui/icons-material/ReportProblemOutlined"

import { billingApi, clientApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import OneOffInvoiceDialog from "./billing/OneOffInvoiceDialog"
import SurchargeRow from "./billing/SurchargeRow"
import BillingSettingsDialog from "./billing/BillingSettingsDialog"
import { surchargeCents } from "./billing/surcharge"
import ClientPaymentMethod from "./billing/ClientPaymentMethod"
import { decimalHours } from "../time/duration"
import { formatDate } from "../utils/format"
import ClientSelect from "../components/ClientSelect"
import InvoiceTable, { money } from "./billing/InvoiceTable"
import RecurringPanel from "./billing/RecurringPanel"

/**
 * Turning hours into an invoice.
 *
 * The screen is the decision in order: pick the client, see what is unbilled and
 * whether it was checked, set a rate per line, read the total, send it. The rate
 * is typed here rather than looked up — it is a commercial decision made per
 * bill, and a stored rate would quietly apply last quarter's number.
 */
export default function BillingPage() {
  const { can } = usePermissions()
  const mayInvoice = can("billing", "invoice")
  const maySchedule = can("billing", "schedule")

  const [tab, setTab] = useState(mayInvoice ? "prepare" : "invoices")
  const [clients, setClients] = useState([])
  const [clientId, setClientId] = useState("")
  const [unbilled, setUnbilled] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [stripe, setStripe] = useState(null)
  const [drafting, setDrafting] = useState(false)
  const [oneOff, setOneOff] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    billingApi.status().then((d) => setStripe(d.stripe)).catch(() => setStripe(null))
    clientApi
      .list()
      .then((d) => setClients((d.organizations || []).filter((o) => String(o.id) !== "0")))
      .catch(() => setClients([]))
  }, [])

  const loadUnbilled = useCallback(async () => {
    if (!clientId) {
      setUnbilled(null)
      return
    }
    setLoading(true)
    try {
      setUnbilled(await billingApi.unbilled({ client_id: clientId }))
      setError(null)
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    loadUnbilled()
  }, [loadUnbilled])

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader title="Billing" subtitle="Turn tracked time into an invoice, and send it through Stripe." />

      {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert> : null}
      {notice ? <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert> : null}

      {/* Said once, at the top, rather than discovered when an invoice will not
          send. The keys are a deployment setting, not something to find out about
          from a failed send. */}
      {stripe && !stripe.configured ? (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          Stripe is not configured on this deployment, so invoices can be drafted but not sent. Set STRIPE_SECRET_KEY to
          enable it.
        </Alert>
      ) : null}
      {stripe?.configured && stripe.mode === "test" ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Stripe is in <strong>test mode</strong> — invoices are real objects in Stripe but no money moves.
        </Alert>
      ) : null}

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        {mayInvoice ? <Tab value="prepare" label="Prepare" /> : null}
        {/* Separate from Prepare because they are different acts. Preparing is
            reading hours and deciding a figure; a recurring charge is a figure
            already agreed, and the screen for it has nothing to weigh up. */}
        {maySchedule ? <Tab value="recurring" label="Recurring" /> : null}
        <Tab value="invoices" label="Invoices" />
      </Tabs>

      {tab === "recurring" && maySchedule ? (
        <RecurringPanel clients={clients} onError={setError} onNotice={setNotice} />
      ) : null}

      {tab === "prepare" && mayInvoice ? (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "flex-end" }}>
                <ClientSelect
                  clients={clients}
                  value={clientId}
                  onChange={setClientId}
                  sx={{ minWidth: 280 }}
                />

                {/* Whether this client can be charged at all, next to the picker
                    rather than three screens away. Choosing "charge
                    automatically" for a client with no card produces an invoice
                    that sits open forever while everybody believes it was
                    collected. */}
                <ClientPaymentMethod clientId={clientId} sx={{ flex: 1 }} />

                {/* Nothing to do with the hours below. A fixed fee, a deposit or
                    a piece of work quoted rather than tracked has no timesheet,
                    and until now the only way to bill one was to set up a
                    recurring charge and stop it after the first run. */}
                <Button variant="outlined" onClick={() => setOneOff(true)} sx={{ flexShrink: 0 }}>
                  One-off invoice
                </Button>
                {/* The rate, the terms and the tax live here. Without somewhere
                    to set them, the fee box on every draft is a control with
                    nothing behind it. */}
                {clientId ? (
                  <Button color="inherit" onClick={() => setSettingsOpen(true)} sx={{ flexShrink: 0 }}>
                    Billing settings
                  </Button>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          {loading ? (
            <Card><CardContent><LoadingRows rows={4} /></CardContent></Card>
          ) : !clientId ? (
            <EmptyState icon={ReceiptIcon} title="Pick a client" description="Choose one above to see what is waiting to be billed." />
          ) : (unbilled?.groups || []).length === 0 ? (
            <EmptyState icon={ReceiptIcon} title="Nothing to bill" description="Every billable hour for this client is already on an invoice." />
          ) : (
            <UnbilledTable
              unbilled={unbilled}
              onDraft={() => setDrafting(true)}
            />
          )}

          <BillingSettingsDialog
            open={settingsOpen}
            clientId={clientId}
            clientLabel={clients.find((c) => String(c.id) === String(clientId))?.label}
            onClose={() => setSettingsOpen(false)}
            onSaved={(message) => {
              setSettingsOpen(false)
              setNotice(message)
              loadUnbilled()
            }}
          />

          <OneOffInvoiceDialog
            open={oneOff}
            clients={clients}
            defaultClientId={clientId}
            onClose={() => setOneOff(false)}
            onError={setError}
            onDrafted={(message) => {
              setOneOff(false)
              setNotice(message)
              setTab("invoices")
            }}
          />

          <DraftInvoiceDialog
            open={drafting}
            clientId={clientId}
            unbilled={unbilled}
            stripe={stripe}
            onClose={() => setDrafting(false)}
            onDrafted={(message) => {
              setDrafting(false)
              setNotice(message)
              setTab("invoices")
              loadUnbilled()
            }}
          />
        </>
      ) : null}

      {/* Named, not defaulted. As the `else` of the Prepare branch this rendered
          on every tab that was not Prepare — so the whole invoice list appeared
          underneath the recurring charges the moment a third tab existed. */}
      {tab === "invoices" ? <InvoiceTable onNotice={setNotice} onError={setError} stripe={stripe} /> : null}
    </Box>
  )
}

/** What is waiting, and how much of it anybody has checked. */
function UnbilledTable({ unbilled, onDraft }) {
  const unsigned = unbilled.totals.unsigned_seconds

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }} flexWrap="wrap">
          <Typography variant="h3" component="p">{decimalHours(unbilled.totals.billable_seconds)} h unbilled</Typography>
          {unsigned > 0 ? (
            <Tooltip title="Sign-off does not block billing — this is here so you know what you are about to send.">
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<WarningIcon sx={{ fontSize: 15 }} />}
                label={`${decimalHours(unsigned)} h not signed off`}
              />
            </Tooltip>
          ) : (
            <Chip size="small" color="success" variant="outlined" label="All hours signed off" />
          )}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<ReceiptIcon />} onClick={onDraft}>
            Draft invoice
          </Button>
        </Stack>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Work</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Signed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unbilled.groups.map((g) => (
              <TableRow key={`${g.project_id}-${g.task_id}`}>
                <TableCell>
                  <Typography variant="body2">{g.suggested_description}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {g.entry_count} {g.entry_count === 1 ? "entry" : "entries"}
                    {g.project_name ? ` · ${g.project_name}` : ""}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDate(g.earliest)} – {formatDate(g.latest)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{g.hours.toFixed(2)}</TableCell>
                <TableCell align="right">
                  {g.unsigned_seconds === 0 ? (
                    <Chip size="small" color="success" label="Yes" sx={{ height: 20, fontSize: 10 }} />
                  ) : (
                    <Chip size="small" color="warning" variant="outlined" label={`${decimalHours(g.signed_seconds)} of ${g.hours.toFixed(2)}`} sx={{ height: 20, fontSize: 10 }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

/** Set a rate per line, read the total, raise the draft. */
function DraftInvoiceDialog({ open, clientId, unbilled, stripe, onClose, onDrafted }) {
  const [rates, setRates] = useState({})
  const [included, setIncluded] = useState({})
  const [notes, setNotes] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // Ticked by default: the card is the common case, and a default of off is a
  // setting somebody forgets and then absorbs.
  const [addSurcharge, setAddSurcharge] = useState(true)

  /* Memoised on `unbilled` rather than computed inline: `|| []` is a new array
     every render, and the effect below resets every rate field — so typing a
     rate would clear the one next to it. */
  const groups = useMemo(() => unbilled?.groups || [], [unbilled])
  const suggested = unbilled?.suggested_rate_cents

  useEffect(() => {
    if (!open) return
    const initialRates = {}
    const initialIncluded = {}
    for (const g of groups) {
      const key = `${g.project_id}-${g.task_id}`
      initialRates[key] = suggested ? String(suggested / 100) : ""
      /* Anything under half a minute rounds to 0.00 hours, and a $0.00 line is a
         line a client asks about. Left in the list so it can be seen and written
         off, but not ticked by default. */
      initialIncluded[key] = g.hours > 0
    }
    setRates(initialRates)
    setIncluded(initialIncluded)
    setNotes("")
    setError(null)
  }, [open, groups, suggested])

  const lines = useMemo(
    () =>
      groups
        .filter((g) => included[`${g.project_id}-${g.task_id}`])
        .map((g) => {
          const key = `${g.project_id}-${g.task_id}`
          const rateCents = Math.round((Number(rates[key]) || 0) * 100)
          return { group: g, key, rateCents, amountCents: Math.round(g.hours * rateCents) }
        }),
    [groups, included, rates],
  )

  const subtotal = lines.reduce((sum, l) => sum + l.amountCents, 0)
  const taxBps = unbilled?.settings?.tax_rate_bps || 0
  const tax = Math.round((subtotal * taxBps) / 10000)
  const missingRate = lines.some((l) => l.rateCents <= 0)

  /* On what the card is actually charged, tax included — the processor takes
     its cut of everything it moves and does not ask which part was tax. */
  const surcharge = addSurcharge
    ? surchargeCents(subtotal + tax, {
        surchargeBps: unbilled?.settings?.surcharge_bps,
        surchargeFixedCents: unbilled?.settings?.surcharge_fixed_cents,
      })
    : 0
  const total = subtotal + tax + surcharge

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const result = await billingApi.createInvoice({
        client_id: clientId,
        lines: lines.map((l) => ({
          kind: "time",
          description: l.group.suggested_description,
          entry_ids: l.group.entry_ids,
          unit_amount_cents: l.rateCents,
          project_id: l.group.project_id,
          task_id: l.group.task_id,
        })),
        notes: notes.trim() || null,
        add_surcharge: addSurcharge,
      })
      onDrafted(`Draft ${result.invoice.number} raised for ${money(result.invoice.total_cents, result.invoice.currency)}.`)
    } catch (err) {
      setError(err.userMessage)
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Draft invoice</DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Line</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right" sx={{ width: 150 }}>Rate / hour</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => {
              const key = `${g.project_id}-${g.task_id}`
              const rateCents = Math.round((Number(rates[key]) || 0) * 100)
              return (
                <TableRow key={key} sx={{ opacity: included[key] ? 1 : 0.45 }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={Boolean(included[key])}
                      onChange={(e) => setIncluded({ ...included, [key]: e.target.checked })}
                      inputProps={{ "aria-label": `Include ${g.suggested_description}` }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{g.suggested_description}</Typography>
                    {g.unsigned_seconds > 0 ? (
                      <Typography variant="caption" color="warning.main">
                        {decimalHours(g.unsigned_seconds)} h not signed off
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{g.hours.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={rates[key] ?? ""}
                      onChange={(e) => setRates({ ...rates, [key]: e.target.value })}
                      disabled={!included[key]}
                      placeholder="0.00"
                      slotProps={{ input: { startAdornment: <InputAdornment position="start">$</InputAdornment> } }}
                      sx={{ width: 130 }}
                      inputProps={{ inputMode: "decimal", "aria-label": `Rate for ${g.suggested_description}` }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {included[key] ? money(Math.round(g.hours * rateCents)) : "—"}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />
        <Stack spacing={0.5} sx={{ ml: "auto", maxWidth: 280 }}>
          <Row label="Subtotal" value={money(subtotal)} />
          {taxBps > 0 ? <Row label={`Tax (${(taxBps / 100).toFixed(2)}%)`} value={money(tax)} /> : null}
          <SurchargeRow
            baseCents={subtotal + tax}
            settings={unbilled?.settings}
            checked={addSurcharge}
            onChange={setAddSurcharge}
            currency={unbilled?.settings?.currency || "USD"}
          />
          <Divider />
          <Row label="Total" value={money(total)} bold />
        </Stack>

        <TextField
          label="Note for the client"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          sx={{ mt: 2 }}
          helperText="Appears on the invoice they receive."
        />

        {stripe && !stripe.configured ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            This will be saved as a draft. Sending it needs Stripe configured.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={submit}
          disabled={busy || lines.length === 0 || missingRate}
        >
          {busy ? "Drafting…" : missingRate ? "Set a rate on every line" : `Draft ${money(total)}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Row({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400} sx={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
    </Stack>
  )
}
